const { debounce } = window.BAO.Utils

class WishlistPage extends window.BAO.CustomElement() {
  static requiredElements () {
    return [
      'loading',
      'pagination',
      'pagination-text',
      'pagination-button',
      'container',
      'display-option',
      'sort-select',
      'search',
      'search-term',
    ]
  }

  async connectedCallback () {
    super.connectedCallback()

    // Setup some default values
    this.layout = this.dataset.layout
    this.page = 1
    this.sort = 'manual'
    this.searchTerm = ''
    this.loading = false
    this.sortable = null

    this.setLoading(true)

    // Request initial page
    this.data = {}
    await this.cleanPageLoad()
  }

  async setupListeners () {
    super.setupListeners()

    // this.onDocumentScroll = this.onDocumentScroll.bind(this)
    this.onDisplayOptionClick = this.onDisplayOptionClick.bind(this)
    this.onSortSelectChange = this.onSortSelectChange.bind(this)
    // this.onPaginationButtonClick = this.onPaginationButtonClick.bind(this)
    this.onSearchSubmit = this.onSearchSubmit.bind(this)
    this.onSearchChange = this.onSearchChange.bind(this)
    this.debouncedOnSearchChange = debounce(
      event => this.onSearchChange(event),
      500,
    )

    this.listeners.add(document, 'scroll', this.onDocumentScroll)

    if (this.els['display-option']) {
      this.els['display-option'].elements.forEach(displayOption => {
        this.listeners.add(displayOption, 'click', this.onDisplayOptionClick)
      })
    }

    if (this.els['sort-select']) {
      this.listeners.add(
        this.els['sort-select'].element,
        'change',
        this.onSortSelectChange,
      )
    }

    if (this.els['pagination-button']) {
      this.listeners.add(
        this.els['pagination-button'].element,
        'click',
        this.onPaginationButtonClick,
      )
    }

    if (this.els.search) {
      this.listeners.add(this.els.search.element, 'submit', this.onSearchSubmit)
      this.listeners.add(
        this.els.search.element.querySelector('input'),
        'keyup',
        this.debouncedOnSearchChange,
      )
    }
  }

  onSearchSubmit (event) {
    event.preventDefault()

    // Do nothing if loading
    if (this.loading) return

    // Set local search term
    this.searchTerm = this.els['search-term'].element.value

    // Request clean page load
    this.cleanPageLoad()
  }

  onSearchChange (e) {
    if (this.searchTerm !== '' && e.target.value === '') {
      // Do nothing if loading
      if (this.loading) return

      // Set local search term
      this.searchTerm = ''

      // Request clean page load
      this.cleanPageLoad()
    }
  }

  async onDisplayOptionClick (event) {
    event.preventDefault()

    // Do nothing if loading
    if (this.loading) return

    // Get the clicked display option value
    const clickedValue = event.currentTarget.dataset.value

    // Set only the clicked option as active
    this.els['display-option'].elements.forEach(displayOption => {
      displayOption.ariaCurrent = displayOption.dataset.value === clickedValue
    })

    // Set local layout value to the clicked value
    this.layout = clickedValue

    // Request clean page load
    await this.cleanPageLoad()
  }

  async onSortSelectChange (event) {
    // Do nothing if loading
    if (this.loading) return

    // Get local sort value to selected value
    this.sort = this.els['sort-select'].element.value

    // Request clean page load
    await this.cleanPageLoad()
  }

  async fetchPage (layout, page, sort = '', format = '') {
    const queryParamsObject = {}
    if (format !== '') queryParamsObject.format = format
    if (this.searchTerm !== '') queryParamsObject.term = this.searchTerm

    /* eslint-disable */
    const queryParams = new URLSearchParams(queryParamsObject).toString()
    /* eslint-enable */

    // Fetch page content
    return await fetch(
      `${window.theme.appSettings.proxyUrl}/lists/${
        this.listId
      }/${layout}/${page}/${sort}${queryParams !== '' ? `?${queryParams}` : ''}`,
    ).then(r => {
      if (!r.ok) throw r
      return format === 'json' ? r.json() : r.text()
    })
  }

  async cleanPageLoad () {
    // Set app to loading
    this.setLoading(true)

    // Clean page load, so set current page to 1
    this.page = 1

    // Clean up the container element
    this.els.container.element.innerHTML = ''
    this.els.container.element.classList.remove('wlt-List_Items-grid')

    // Display loading element
    this.els.loading.element.style.display = 'flex'
    this.els.pagination.element.style.display = 'none'

    // Set grid class if requested layout is grid
    if (this.layout === 'grid') {
      this.els.container.element.classList.add('wlt-List_Items-grid')
    }

    // Fetch the first page
    this.data = await this.fetchPage(this.layout, this.page, this.sort, 'json')
    this.els.container.element.innerHTML = await this.fetchPage(
      this.layout,
      this.page,
      this.sort,
    )

    // Set pagination data
    this.setPaginationData()

    // Hide loading element
    this.els.loading.element.style.display = 'none'
    this.els.pagination.element.style.display = 'flex'

    // Load the rest of the pages
    await this.loadPages()

    // Set app as loaded
    this.setLoading(false)
  }

  async loadPages () {
    // If we are at the last page, nothing to load
    if (this.page >= this.lastPage) {
      await this.finishedLoadingPages()
      return
    }

    // Increment local page
    this.page++

    // Append next page
    this.els.container.element.innerHTML += await this.fetchPage(
      this.layout,
      this.page,
      this.sort,
    )

    // Set pagination data
    this.setPaginationData()

    return this.loadPages()
  }

  setPaginationData () {
    const paginationPhrase = this.els['pagination-text'].element.dataset.phrase
    this.els['pagination-text'].element.innerText = paginationPhrase
      .replace('{{ product_count }}', this.paginationCount)
      .replace('{{ total_product_count }}', this.data.total)

    this.els.pagination.element.style.setProperty(
      '--Bar_Percentage',
      `${this.paginationPercentage}%`,
    )

    if (this.page === this.lastPage) {
      this.els.pagination.element.classList.remove('wlt-Pagination-enabled')
    } else {
      this.els.pagination.element.classList.add('wlt-Pagination-enabled')
    }
  }

  setLoading (loading) {
    this.loading = loading

    if (loading) {
      if (this.els['sort-select']) {
        this.els['sort-select'].element.disabled = true
      }
      if (this.els['search-term']) {
        this.els['search-term'].element.disabled = true
      }
      if (this.els['pagination-button']) {
        this.els['pagination-button'].element.disabled = true
      }
    } else {
      if (this.els['sort-select']) {
        this.els['sort-select'].element.disabled = false
      }
      if (this.els['search-term']) {
        this.els['search-term'].element.disabled = false
      }
      if (this.els['pagination-button']) {
        this.els['pagination-button'].element.disabled = false
      }
    }
  }

  async finishedLoadingPages () {
    if (!this.sortable) {
      // eslint-disable-next-line no-undef
      this.sortable = new Draggable.Sortable(
        document.querySelectorAll('.wlt-List_Items'),
        {
          draggable: '.wlt-List_Item',
          handle: '.wlt-ItemRow_Grab',
          classes: {
            'source:dragging': 'wlt-List_Item-faded',
            'source:original': 'wlt-List_Item-faded',
            mirror: 'wlt-List_Item-mirror',
          },
          delay: {
            mouse: 0,
            drag: 100,
            touch: 100,
          },
          mirror: {
            constrainDimensions: true,
          },
          // eslint-disable-next-line no-undef
          plugins: [Draggable.Plugins.SortAnimation],
          swapAnimation: {
            duration: 200,
            easingFunction: 'ease-in-out',
          },
        },
      )

      const event = new Event('wishlist-pages-loaded')
      window.dispatchEvent(event)

      this.sortable.on('drag:stopped', this.onDragEnd.bind(this))
    }
  }

  async onDragEnd (event) {
    const source = event.data.originalSource
    const targetId = source.dataset.id
    const previousId = source.previousElementSibling?.dataset.id

    await window.BAO.wishlist.reorderItems(
      this.listId,
      window.BAO.wishlist.token,
      {
        targetId,
        previousId,
      },
    )
  }

  get listId () {
    return this.dataset.listId
  }

  get paginationCount () {
    return Math.max(
      0,
      Math.min((this.data?.perPage || 0) * this.page, this.data?.total || 0),
    )
  }

  get paginationPercentage () {
    return (this.paginationCount / (this.data?.total || 0)) * 100
  }

  get lastPage () {
    return Math.ceil((this.data?.total || 0) / (this.data?.perPage || 0))
  }
}

customElements.define('wishlist-page', WishlistPage)
